// ui/pages/SealApplicationPage.tsx
// v6.5 — 用印申請：套用「合約簽核新制」同樣的互動模式
// - 列表頁：新增 / 檢視 / 編輯 / 簽核流程 /（超管）刪除
// - 駁回/退回後：原起單人可重新編輯修正後再送

import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import type { ApprovalRequest } from "../types/legalOps";

import {
  listApprovalRequestsByModule,
  deleteApprovalRequest,
} from "../services/approvalRequestsService";
import {
  createApprovalRequest,
  updateApprovalRequest,
  uploadApprovalFile,
} from "../services/approvalRequestsService";

import { useToast } from "../components/Toast";
import { useAiContext } from "../ai/AiContext";
import { Modal } from "../components/Modal";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { AiFieldHeader } from "../components/shared/AiFieldHeader";

import { analyzeUploadedFile } from "../services/aiService";

import { createOrResetApprovalFlow, resetFlowForResubmission, updateStageDecision } from "../services/approvalFlowService";
import { ApprovalFlowPanel } from "../components/approval/ApprovalFlowPanel";
import { useApprovalFlow } from "../hooks/useApprovalFlow";

import { isSuperAdmin, isSuperUser, isReadonlySuperUser } from "../config/roles";
import { db } from "../../firebaseClient";
import { generateDocCode } from "../utils/idGenerator";

// ---- Display helpers ----
// 「歷史」= 僅已完成流程
const HISTORY_STATUSES = ["FULLY_APPROVED", "PUBLISHED", "COMPLETED"];

function getStatusLabel(status: string) {
  switch (status) {
    case "SUBMITTED":
      return "已送件";
    case "IN_REVIEW":
      return "簽核中";
    case "WAITING_APPROVAL":
    case "PENDING_MANAGER":
      return "主管核決中";
    case "APPROVED":
      return "已核准";
    case "FULLY_APPROVED":
    case "COMPLETED":
      return "流程完成";
    case "REJECTED":
      return "已駁回";
    case "RETURNED":
    case "LEGAL_RETURNED":
      return "退回";
    case "DRAFT":
      return "草稿";
    case "CANCELLED":
      return "已作廢";
    default:
      return status || "未知";
  }
}

function getStatusColor(status: string) {
  if (status === "FULLY_APPROVED" || status === "APPROVED" || status === "COMPLETED" || status === "PUBLISHED")
    return "var(--ok)";
  if (status === "REJECTED" || status === "RETURNED" || status === "LEGAL_RETURNED" || status === "CANCELLED")
    return "var(--danger)";
  if (status === "IN_REVIEW" || status === "WAITING_APPROVAL" || status === "PENDING_MANAGER") return "#fbbf24";
  return "#999";
}

function isLockedByInReview(status?: string) {
  const s = status || "";
  return ["IN_REVIEW", "WAITING_APPROVAL", "PENDING_REVIEW", "PENDING_MANAGER", "PENDING_LEGAL"].includes(s);
}

function isRejectedLike(status?: string) {
  const s = status || "";
  return ["REJECTED", "RETURNED", "LEGAL_RETURNED"].includes(s);
}

// ---- Page ----
export default function SealApplicationPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { requestDelete } = useAiContext();

  const [items, setItems] = useState<ApprovalRequest[]>([]);
  const [subTab, setSubTab] = useState<"ACTIVE" | "HISTORY">("ACTIVE");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ApprovalRequest | null>(null);
  const [formReadOnly, setFormReadOnly] = useState(false);

  const [flowViewItem, setFlowViewItem] = useState<ApprovalRequest | null>(null);

  useEffect(() => {
    if (!user) return;
    const unsub = listApprovalRequestsByModule("sealRequest" as any, (all) => setItems(all));
    return () => {
      unsub.then((fn) => fn());
    };
  }, [user]);

  const canDelete = (email?: string | null) => isSuperUser(email) || isSuperAdmin(email);
  const isReadonly = isReadonlySuperUser(user?.email);

  const activeList = useMemo(() => {
    return items.filter((i) => {
      if (i.isDeleted) return false;
      if (subTab === "HISTORY") return HISTORY_STATUSES.includes(i.status);
      return !HISTORY_STATUSES.includes(i.status);
    });
  }, [items, subTab]);

  const openCreate = () => {
    setEditingItem(null);
    setFormReadOnly(false);
    setIsFormOpen(true);
  };

  const openView = (item: ApprovalRequest) => {
    setEditingItem(item);
    setFormReadOnly(true);
    setIsFormOpen(true);
  };

  const openEdit = (item: ApprovalRequest) => {
    // Default editable, but guard for in-review lock.
    const locked = isLockedByInReview(item.status);
    setEditingItem(item);
    setFormReadOnly(locked);
    setIsFormOpen(true);
  };

  const handleDelete = (req: ApprovalRequest) => {
    requestDelete({
      targetType: "sealRequest",
      targetId: req.id,
      summary: req.title,
      onConfirm: async () => {
        try {
          await deleteApprovalRequest(req.id);
          showToast("已刪除（或標記為刪除）");
        } catch (e: any) {
          showToast(e?.message || "刪除失敗", "error");
        }
      },
    });
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div>
          <h2 style={{ margin: 0 }}>用印申請</h2>
          <div className="p" style={{ marginTop: 6, color: "var(--muted)" }}>
            新制：檢視 / 簽核流程分離；駁回後可重新編輯修正再送。
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="smallbtn" onClick={() => setSubTab("ACTIVE")} style={{ opacity: subTab === "ACTIVE" ? 1 : 0.7 }}>
            用印申請
          </button>
          <button className="smallbtn" onClick={() => setSubTab("HISTORY")} style={{ opacity: subTab === "HISTORY" ? 1 : 0.7 }}>
            歷史紀錄
          </button>
          <button className="btn" onClick={openCreate} disabled={!user || isReadonly} title={isReadonly ? "唯讀超級帳號禁止寫入" : ""}>
            新增用印申請
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {activeList.length === 0 ? (
          <div className="p" style={{ color: "var(--muted)", padding: 12 }}>
            目前沒有資料。
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {activeList.map((it) => (
              <div
                key={it.id}
                style={{
                  border: "1px solid #333",
                  borderRadius: 10,
                  padding: 12,
                  background: "rgba(255,255,255,0.03)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          color: getStatusColor(it.status),
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        ● {getStatusLabel(it.status)}
                      </span>
                      <span style={{ color: "var(--muted)", fontSize: 12 }}>
                        {it.department || "—"}
                      </span>
                      {isRejectedLike(it.status) && (
                        <span style={{ color: "#fbbf24", fontSize: 12, fontWeight: 600 }}>
                          （可修正後重新送出）
                        </span>
                      )}
                    </div>

                    <div style={{ marginTop: 6, fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {it.title || "(未命名)"}
                    </div>

                    <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>
                      申請人：{it.requesterName || "—"}　｜　單號：{it.docCode || it.id.slice(0, 8)}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button className="smallbtn" onClick={() => openView(it)}>
                      檢視
                    </button>
                    <button
                      className="smallbtn"
                      onClick={() => openEdit(it)}
                      disabled={isReadonly}
                      title={isReadonly ? "唯讀超級帳號禁止寫入" : isLockedByInReview(it.status) ? "簽核中不可編輯" : ""}
                    >
                      編輯
                    </button>
                    <button className="smallbtn" onClick={() => setFlowViewItem(it)}>
                      簽核流程
                    </button>
                    {canDelete(user?.email) && (
                      <button className="smallbtn" style={{ color: "var(--danger)" }} onClick={() => handleDelete(it)}>
                        刪除
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ Form Modal */}
      {isFormOpen && (
        <SealRequestFormModal
          initialData={editingItem}
          allExisting={items}
          forceReadOnly={formReadOnly}
          onClose={() => setIsFormOpen(false)}
          onSuccess={() => {
            setIsFormOpen(false);
          }}
        />
      )}

      {/* ✅ Flow Modal */}
      {flowViewItem && (
        <SealApprovalFlowViewModal request={flowViewItem} onClose={() => setFlowViewItem(null)} />
      )}
    </div>
  );
}

function SealApprovalFlowViewModal({ request, onClose }: { request: ApprovalRequest; onClose: () => void }) {
  const flowModule = "SEAL_REQUEST";
  const flowDocId = (request as any).approvalFlowId || (request as any).flowId || undefined;
  const { flow, loading } = useApprovalFlow(flowModule as any, request.id, flowDocId);
  const { user } = useAuth();

  return (
    <Modal title={`簽核流程：${request.docCode || request.id.slice(0, 8)}｜${request.title}`} onClose={onClose} width={820}>
      <div style={{ padding: 12 }}>
        {loading ? (
          <div className="p" style={{ padding: 12, color: "var(--muted)" }}>
            載入簽核流程中…
          </div>
        ) : (
          <ApprovalFlowPanel
          flow={(flow ?? null) as any}
          mode="action"
          onDecision={async (stageKey, decision, comment, deptCode, isDirectSign) => {
            if (!flow || !user) return;
            const actorUid = (user as any)?.uid || (user as any)?.id || "";
            const actorName = (user as any)?.displayName || (user as any)?.name || user?.email || "User";
            const actorEmail = user?.email || "";
            const mappedDecision: any = (decision as any) === "APPROVED_AND_SEAL" ? "APPROVED" : decision;
            const fid = (flow as any).id || `${(flow as any).module || flowModule}_${(flow as any).targetId || request.id}`;
            await updateStageDecision({
              flowId: fid,
              stageKey,
              decision: mappedDecision,
              comment: comment || "",
              actorUid,
              actorName,
              actorEmail,
              deptCode,
              isDirectSign
            } as any);
          }}
        />
        )}
      </div>
    </Modal>
  );
}

function SealRequestFormModal({
  initialData,
  allExisting,
  forceReadOnly,
  onClose,
  onSuccess,
}: {
  initialData: ApprovalRequest | null;
  allExisting?: ApprovalRequest[];
  forceReadOnly?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const [form, setForm] = useState({
    docCode: (initialData as any)?.docCode || initialData?.docCode || "",
    title: initialData?.title || "",
    department: initialData?.department || "",
    sealType: (initialData as any)?.sealType || "",
    copyCount: (initialData as any)?.copyCount || 1,
    docCategory: (initialData as any)?.docCategory || "",
    description: initialData?.description || "",
  });

  const [uploadedAttachments, setUploadedAttachments] = useState<any[]>(initialData?.attachments || []);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);


  // AUTO_DOC_CODE_SEAL: 新增用印申請時自動產生單號（docCode）
  useEffect(() => {
    if (initialData) return; // 編輯/檢視時不改單號
    setForm((prev) => {
      if (prev.docCode) return prev;
      const code = generateDocCode("seal", new Date(), allExisting || []);
      return { ...prev, docCode: code };
    });
  }, [initialData, allExisting]);


  const sealTypeOptions = [
    { value: "COMPANY_SEAL", label: "公司大章" },
    { value: "COMPANY_BOTH_SEALS", label: "公司大小章" },
    { value: "RESPONSIBLE_PERSON_SEAL", label: "負責人小章" },
    { value: "INVOICE_SEAL", label: "發票章" },
    { value: "CONTRACT_SEAL", label: "合約章" },
    { value: "LEGAL_SEAL", label: "法務章" },
    { value: "OTHER", label: "其他" },
  ];

  const sealCategoryOptions = [
    { value: "CONTRACT", label: "合約" },
    { value: "INVOICE", label: "發票" },
    { value: "LETTER", label: "函文" },
    { value: "REPORT", label: "報告" },
    { value: "OTHER", label: "其他" },
  ];

  const status = initialData?.status || "DRAFT";
  const isResubmitting = !!initialData && isRejectedLike(status);

  const isRequester = (initialData as any)?.requesterUid && (user as any)?.uid && (initialData as any)?.requesterUid === (user as any)?.uid;
  const canEditAfterReject = isResubmitting && isRequester;

  const lockedByReview = isLockedByInReview(status);
  const isLocked = !!forceReadOnly || (lockedByReview && !canEditAfterReject);

  const validateRequired = () => {
    if (!form.title || !form.sealType || !form.docCategory) {
      showToast("請填寫：標題、印鑑類型、文件類別", "warning");
      return false;
    }
    return true;
  };

  const normalizeSealType = (v: any) => {
    if (!v) return "";
    const s = String(v).toUpperCase();
    if (
      [
        "COMPANY_SEAL",
        "COMPANY_BOTH_SEALS",
        "RESPONSIBLE_PERSON_SEAL",
        "INVOICE_SEAL",
        "CONTRACT_SEAL",
        "LEGAL_SEAL",
        "OTHER",
      ].includes(s)
    )
      return s;
    const raw = String(v);
    if (raw.includes("公司大小章") || raw.includes("大小章")) return "COMPANY_BOTH_SEALS";
    if (raw.includes("負責人")) return "RESPONSIBLE_PERSON_SEAL";
    if (raw.includes("公司")) return "COMPANY_SEAL";
    if (raw.includes("發票")) return "INVOICE_SEAL";
    if (raw.includes("合約")) return "CONTRACT_SEAL";
    if (raw.includes("法務")) return "LEGAL_SEAL";
    return "OTHER";
  };

  const normalizeDocCategory = (v: any) => {
    if (!v) return "";
    const s = String(v).toUpperCase();
    if (["CONTRACT", "INVOICE", "LETTER", "REPORT", "OTHER"].includes(s)) return s;
    const raw = String(v);
    if (raw.includes("合約")) return "CONTRACT";
    if (raw.includes("發票")) return "INVOICE";
    if (raw.includes("函")) return "LETTER";
    if (raw.includes("報告")) return "REPORT";
    return "OTHER";
  };

  const handleAiFill = async () => {
    const file = pendingFiles?.[0];
    const fileMeta = !file ? uploadedAttachments?.[0] : undefined;
    if (!file && !fileMeta) {
      showToast("請先上傳至少一份附件，再使用 AI 助手", "warning");
      return;
    }
    try {
      setAiLoading(true);
      const result = await analyzeUploadedFile({
        mode: "analysis",
	        featureId: "sealApplication.analysis",
        file,
        fileMeta,
        documentType: "seal_application",
        userInstruction:
          "請讀取附件內容並回傳 JSON 欄位：title(建議標題)、sealType(公司大章/公司大小章/負責人小章/發票章/合約章/法務章/其他)、docCategory(合約/發票/函文/報告/其他)、copyCount(數字)、description(摘要與注意事項，繁中)。只回傳 JSON。",
      });

	      const data: any = (result as any) || {};
	      const riskEvalText = String((result as any)?.riskAssessmentText || "").trim();
      setForm((prev) => ({
        ...prev,
        title: prev.title || data.title || "",
        sealType: prev.sealType || normalizeSealType(data.sealType),
        docCategory: prev.docCategory || normalizeDocCategory(data.docCategory),
        copyCount: typeof data.copyCount === "number" ? data.copyCount : prev.copyCount,
	        description: (() => {
	          const desc = String(data.description || "").trim();
	          const merged = [desc, riskEvalText].filter(Boolean).join("\n\n");
	          if (!merged) return prev.description;
	          return prev.description ? `${prev.description}\n\n${merged}` : merged;
	        })(),
      }));
      showToast("AI 建議已帶入（可再手動調整）", "success");
    } catch (err: any) {
      showToast(err?.message || "AI 分析失敗", "error");
    } finally {
      setAiLoading(false);
    }
  };

  const uploadPendingFilesIfAny = async (docId: string) => {
    let finalAttachments = [...uploadedAttachments];
    if (pendingFiles.length === 0) return finalAttachments;

    for (const file of pendingFiles) {
      finalAttachments.push(await uploadApprovalFile(docId, "request", file, user?.email || ""));
    }
    setPendingFiles([]);
    setUploadedAttachments(finalAttachments);
    return finalAttachments;
  };

  const handleSave = async (submitNow: boolean) => {
    if (!user) {
      showToast("尚未登入", "error");
      return;
    }
    if (isReadonlySuperUser(user?.email)) {
      showToast("權限不足：唯讀超級帳號禁止寫入", "error");
      return;
    }
    if (isLocked) {
      showToast("案件簽核中，無法修改。", "warning");
      return;
    }
    if (submitNow && !validateRequired()) return;

    setLoading(true);
    try {
      const basePayload: Partial<ApprovalRequest> & any = {
        docCode: (initialData as any)?.docCode || form.docCode || generateDocCode("seal", new Date(), allExisting || []),
        title: form.title,
        department: form.department,
        description: form.description,
        sealType: form.sealType,
        copyCount: Number(form.copyCount) || 1,
        docCategory: form.docCategory,
        module: "sealRequest",
        historyPolicyVersion: 2,
        updatedAt: new Date().toISOString(),
      };

      let docId = initialData?.id;

      if (docId) {
        // Update existing
        const finalAttachments = await uploadPendingFilesIfAny(docId);
        await updateApprovalRequest(docId, {
          ...(basePayload as any),
          attachments: finalAttachments,
          status: submitNow ? "IN_REVIEW" : (initialData?.status || "DRAFT"),
        } as any);

        // 🔁 Resubmission flow reset
        if (submitNow && canEditAfterReject) {
          await resetFlowForResubmission(`SEAL_REQUEST_${docId}`);
          await db.collection("approval_requests").doc(docId).update({
            resubmittedAt: new Date().toISOString(),
            resubmittedBy: user?.displayName || "User",
            status: "IN_REVIEW",
          });
        }

        // New submission from draft: ensure flow exists
        if (submitNow && !isResubmitting) {
          await createOrResetApprovalFlow(
            "SEAL_REQUEST" as any,
            docId,
            (user as any)?.id || (user as any)?.uid || "",
            form.department,
            user?.displayName || user?.email || "User"
          );
        }
      } else {
        // Create new
        const newId = await createApprovalRequest({
          ...(basePayload as any),
          requesterUid: (user as any)?.id || (user as any)?.uid || "",
          requesterName: user?.displayName || user?.email || "User",
          requesterEmail: user?.email || "",
          sensitivity: "normal",
          priority: "normal",
          attachments: [],
          status: submitNow ? "IN_REVIEW" : "DRAFT",
        } as any);
        docId = newId;

        const finalAttachments = await uploadPendingFilesIfAny(docId);
        if (finalAttachments.length > 0) {
          await updateApprovalRequest(docId, { attachments: finalAttachments } as any);
        }

        if (submitNow) {
          await createOrResetApprovalFlow(
            "SEAL_REQUEST" as any,
            docId,
            (user as any)?.id || (user as any)?.uid || "",
            form.department,
            user?.displayName || user?.email || "User"
          );
        }
      }

      showToast(submitNow ? "已送出簽核" : "草稿已儲存");
      onSuccess();
    } catch (e: any) {
      console.error(e);
      showToast(`儲存失敗：${e?.message || e}`, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        status === "IN_REVIEW"
          ? "簽核中（不可變更）"
          : isResubmitting
          ? "駁回/退回後修正補充"
          : initialData
          ? (forceReadOnly ? "檢視用印申請" : "編輯用印申請")
          : "新增用印申請"
      }
      onClose={onClose}
      width={850}
    >
      <div style={{ padding: 16 }}>
        {isLocked && lockedByReview && !canEditAfterReject && (
          <div
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid #333",
              borderRadius: 10,
              padding: 12,
              marginBottom: 12,
              color: "#e5e7eb",
              fontSize: 13,
            }}
          >
            簽核中・此為唯讀檢視模式，表單內容不可修改。
          </div>
        )}

        {!isLocked && isResubmitting && (
          <div
            style={{
              background: "rgba(251,191,36,0.12)",
              border: "1px solid rgba(251,191,36,0.35)",
              borderRadius: 10,
              padding: 12,
              marginBottom: 12,
              color: "#fbbf24",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            此案件已駁回/退回：請修正內容後再重新送出簽核。
          </div>
        )}


        {/* DISPLAY_DOC_CODE_SEAL */}
        <div style={{ marginBottom: 8, fontSize: 12, color: "var(--muted)" }}>
          單號：{(initialData as any)?.docCode || initialData?.docCode || form.docCode || "（待產生）"}
        </div>

        <div className="grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="field">
            <label className="label">標題</label>
            <input
              className="input"
              value={form.title}
              disabled={isLocked || loading}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="例：供應商合約用印"
            />
          </div>

          <div className="field">
            <label className="label">申請部門</label>
            <input
              className="input"
              value={form.department}
              disabled={isLocked || loading}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              placeholder="例：行銷部"
            />
          </div>

          <div className="field">
            <label className="label">印鑑類型</label>
            <select
              className="input"
              value={form.sealType}
              disabled={isLocked || loading}
              onChange={(e) => setForm({ ...form, sealType: e.target.value })}
            >
              <option value="">請選擇</option>
              {sealTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="label">份數</label>
            <input
              className="input"
              type="number"
              min={1}
              value={form.copyCount}
              disabled={isLocked || loading}
              onChange={(e) => setForm({ ...form, copyCount: Number(e.target.value) })}
            />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">文件類別</label>
            <select
              className="input"
              value={form.docCategory}
              disabled={isLocked || loading}
              onChange={(e) => setForm({ ...form, docCategory: e.target.value })}
            >
              <option value="">請選擇</option>
              {sealCategoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <AiFieldHeader
              label="說明"
              onClick={handleAiFill}
              disabled={isLocked || loading}
              loading={aiLoading}
              hint="上傳附件後，可用 AI 自動產出摘要/建議填入欄位"
            />
            <textarea
              className="input"
              value={form.description}
              disabled={isLocked || loading}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="補充說明（可選）"
              rows={5}
              style={{ resize: "vertical" }}
            />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <FileUploadKit
            label="附件上傳"
            description={isLocked ? "簽核中不可新增/刪除附件" : "拖曳檔案到此，或點擊選擇（可多選）"}
            disabled={isLocked || loading}
            existingFiles={uploadedAttachments as any}
            rawFiles={pendingFiles}
            onFilesSelected={(files) => setPendingFiles((prev) => [...prev, ...files])}
            onRemoveRawFile={(idx) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button className="smallbtn" onClick={onClose} disabled={loading}>
            關閉
          </button>

          {!forceReadOnly && (
            <>
              <button className="smallbtn" onClick={() => handleSave(false)} disabled={loading || isLocked}>
                儲存草稿
              </button>
              <button className="btn" onClick={() => handleSave(true)} disabled={loading || isLocked}>
                送出簽核
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
